import { type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useRouteLocale } from "@/hooks/useRouteLocale";
import { ADMIN_ROUTES, USER_ROUTES } from "@/routes";

interface RequireSuperadminProps {
  children: ReactNode;
}

/**
 * Route guard for `/management/*` routes (JUB-222).
 * Redirects unauthenticated visitors to `/{locale}/management/login`
 * and authenticated non-admin users to their customer dashboard.
 */
export function RequireSuperadmin({ children }: RequireSuperadminProps) {
  const { user, isAuthenticated, loading } = useAuth();
  const currentLocale = useRouteLocale();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={ADMIN_ROUTES.login(currentLocale)} replace />;
  }

  if (!user?.user_admin) {
    return <Navigate to={USER_ROUTES.events(currentLocale)} replace />;
  }

  return <>{children}</>;
}
